import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { phoneTest } from "@/utils/phoneValidation";
import { Spinner } from "@/components/ui/spinner";
import { useForm, useWatch } from "react-hook-form";
import { usePractice } from "../api/get-practice";
import { useUpdatePractice } from "../api/update-practice";
import { PracticeForm } from "./practice-form";

const schema = yup
    .object({
        practiceTitle: yup.string().required("Practice name is required"),
        addressNumber: yup.string().when("isAddressSameAsPersonal", {
            is: (val) => val === false,
            then: (schema) =>
                schema.required(
                    "Please enter your practice's address number/name",
                ),
            otherwise: (schema) => schema.notRequired(),
        }),
        addressLine1: yup.string().when("isAddressSameAsPersonal", {
            is: (val) => val === false,
            then: (schema) =>
                schema.required(
                    "Please enter the first line of your practice's address",
                ),
            otherwise: (schema) => schema.notRequired(),
        }),
        addressTown: yup.string().when("isAddressSameAsPersonal", {
            is: (val) => val === false,
            then: (schema) =>
                schema.required("Please enter the town of your practice"),
            otherwise: (schema) => schema.notRequired(),
        }),
        addressCounty: yup.string().when("isAddressSameAsPersonal", {
            is: (val) => val === false,
            then: (schema) =>
                schema.required("Please enter the county of your practice"),
            otherwise: (schema) => schema.notRequired(),
        }),
        addressPostalCode: yup.string().when("isAddressSameAsPersonal", {
            is: (val) => val === false,
            then: (schema) =>
                schema.required("Please enter the postcode of your practice"),
            otherwise: (schema) => schema.notRequired(),
        }),
        workArrangement: yup.string().required("Please select an option"),
        adminName: yup.string().when("isGroupPractice", {
            is: (val) => val === true,
            then: (schema) =>
                schema.required("Please enter your admins' email address"),
            otherwise: (schema) => schema.notRequired(),
        }),
        adminEmail: yup.string().when("isGroupPractice", {
            is: (val) => val === true,
            then: (schema) =>
                schema
                    .required("Please enter your admins' email address")
                    .email("Enter an email like name@example.com"),
            otherwise: (schema) => schema.notRequired(),
        }),
        adminPhoneNumber: yup.string().when("isGroupPractice", {
            is: (val) => val === true,
            then: (schema) =>
                schema
                    .required("Please enter your admins' phone number")
                    .test(phoneTest("GB")),
            otherwise: (schema) => schema.notRequired(),
        }),
    })
    .required();

export const PracticeUpdate = () => {
    const { data: practice, isLoading } = usePractice();

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            isGroupPractice: false,
        },
        values: practice,
    });

    // Show/hide sections
    const isAddressSameAsPersonal = useWatch({
        control: methods.control,
        name: "isAddressSameAsPersonal",
    });

    const isGroupPractice = useWatch({
        control: methods.control,
        name: "isGroupPractice",
    });

    // Submit
    const mutation = useUpdatePractice();

    const onSubmit = (data) => {
        mutation.mutate(data);
    };

    if (isLoading) {
        return (
            <div className="relative">
                <div className="absolute inset-0 z-10 py-10">
                    <div className="grid h-75 place-items-center">
                        <Spinner size="lg" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <PracticeForm
            methods={methods}
            onSubmit={onSubmit}
            isAddressSameAsPersonal={isAddressSameAsPersonal}
            isGroupPractice={isGroupPractice}
            mutation={mutation}
        />
    );
};
